import { Component, Input, OnDestroy } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { DwContextSubjects, DwMetaDataFormConfig, DwSectionComponent } from '@devwareapps/devware-cap';
import { Subscription } from 'rxjs';
import { AppearancePresetEntity, ChallengeTeamEntity } from '../../../../meta-data/app-meta-data.service';
import { ChallengeContextService } from '../../contexts/challenge-context/challenge-context.service';
import { AppearancePresetsService } from '../../services/appearance/appearance-presets.service';
import { AppearanceSettings, BannerEffect, availablePresets, avatarContrast, resolveAppearance, validColor } from '../../services/appearance/appearance';

@DwSectionComponent({ keyName: 'fitnessAppearanceEditor', display: 'Appearance picker', sectionIcon: 'fa fa-palette', hasChildren: true, childrenPropertyName: 'sections' })
@Component({ selector: 'app-appearance-editor', templateUrl: './appearance-editor.component.html', styleUrls: ['./appearance-editor.component.scss'] })
export class AppearanceEditorComponent implements OnDestroy {
    @Input() sectionConfig: { team?: boolean; isReadOnly?: boolean; sections?: any[]; previewName?: string; avatarMediaId?: number; bannerMediaId?: number };
    contexts: DwContextSubjects;
    @Input() formConfig: DwMetaDataFormConfig;
    @Input() formGroup: FormGroup;
    @Input() submitted: boolean;
    @Input() sectionReadOnly = false;
    @Input() parentIsVisible = true;
    @Input() visible = true;
    paletteOpen = false;
    private _mode: 'preset' | 'custom';
    private _customDraft: AppearanceSettings;
    private _presetId: number;
    // Renderer keys are supported by the deployed animation catalog.
    readonly effects: { key: BannerEffect; label: string }[] = [{ key: 'none', label: 'Off' }, { key: 'fireflies', label: 'Fireflies' }, { key: 'snowfall', label: 'Snowfall' }, { key: 'christmas-lights', label: 'Christmas lights' }];
    data: Pick<ChallengeTeamEntity, 'ChallengeDefinitionId' | 'TeamName' | 'TeamPictureMediaId' | 'TeamBackgroundMediaId'> = {};
    private _subscriptions = new Subscription();
    private _formReadOnly = false;
    constructor(public catalog: AppearancePresetsService, private challenge: ChallengeContextService) {}
    @Input() set context(value: DwContextSubjects) {
        this.contexts = value;
        this._subscriptions.unsubscribe(); this._subscriptions = new Subscription();
        this.data = {}; this._formReadOnly = false;
        this.paletteOpen = false;
        this._mode = null; this._customDraft = null; this._presetId = null;
        if (value?.data) { this._subscriptions.add(value.data.subscribe(data => this.data = data || {})); }
        if (value?.mdFormContext) { this._subscriptions.add(value.mdFormContext.subscribe(form => this._formReadOnly = !!form?.isReadOnly)); }
    }
    get readOnly(): boolean { return this.sectionConfig?.isReadOnly || this.sectionReadOnly || this._formReadOnly || this.formGroup?.disabled; }
    get settings(): AppearanceSettings { return this.formGroup?.getRawValue() || {}; }
    get customize(): boolean {
        if (this._mode) { return this._mode === 'custom'; }
        const settings = this.settings;
        return !settings.AppearancePresetId || ['BannerColorStart', 'BannerColorEnd', 'AvatarBackgroundColor', 'AvatarTextColor']
            .some(field => !!settings[field]) || settings.BannerUseGradient != null;
    }
    get previewSettings(): AppearanceSettings {
        return this.settings;
    }
    get previewName(): string { return this.data.TeamName || this.sectionConfig?.previewName || (this.sectionConfig?.team ? 'Your team' : 'Your look'); }
    get avatarMediaId(): number { const control = this.formGroup?.get('TeamPictureMediaId'); return control ? control.value : this.data.TeamPictureMediaId ?? this.sectionConfig?.avatarMediaId; }
    get bannerMediaId(): number { const control = this.formGroup?.get('TeamBackgroundMediaId'); return control ? control.value : this.data.TeamBackgroundMediaId ?? this.sectionConfig?.bannerMediaId; }
    options(presets: AppearancePresetEntity[]): AppearancePresetEntity[] {
        return availablePresets(presets || [], !!this.sectionConfig?.team, this.sectionConfig?.team ? this.data.ChallengeDefinitionId : this.challenge.currentChallenge?.ChallengeDefinitionId);
    }
    private _colorOptionsSource: AppearancePresetEntity[];
    private _colorOptionsScope = '';
    private _colorOptions: { label: string; value: number; preset: AppearancePresetEntity }[] = [];
    colorOptions(presets: AppearancePresetEntity[]) {
        const scope = `${!!this.sectionConfig?.team}:${this.sectionConfig?.team ? this.data.ChallengeDefinitionId : this.challenge.currentChallenge?.ChallengeDefinitionId}`;
        if (presets !== this._colorOptionsSource || scope !== this._colorOptionsScope) {
            this._colorOptionsSource = presets; this._colorOptionsScope = scope;
            this._colorOptions = [...this.options(presets).map(p => ({ label: p.Name, value: p.AppearancePresetId, preset: p })),
                { label: 'Custom colors', value: null, preset: null }];
        }
        return this._colorOptions;
    }
    selectColor(id: number, presets: AppearancePresetEntity[]): void { const preset = this.options(presets).find(p => p.AppearancePresetId === id); if (preset) { this.choose(preset, presets); } else { this.custom(presets); } }
    preset(presets: AppearancePresetEntity[]): AppearancePresetEntity { return presets?.find(p => p.AppearancePresetId === this.settings.AppearancePresetId); }
    look(presets: AppearancePresetEntity[]) { return resolveAppearance(this.settings, this.preset(presets)); }
    palette(preset: AppearancePresetEntity) { return resolveAppearance({}, preset); }
    imageNotice(presets: AppearancePresetEntity[]): string {
        const look = resolveAppearance(this.settings, this.preset(presets), this.avatarMediaId, this.bannerMediaId);
        if (look.bannerMediaId && look.avatarMediaId) { return 'Your banner and avatar images will show instead of these colors.'; }
        if (look.bannerMediaId) { return 'Your banner image will show instead of the banner colors.'; }
        if (look.avatarMediaId) { return 'Your avatar image will show instead of the avatar colors.'; }
        return '';
    }
    useGradient(presets: AppearancePresetEntity[]): boolean {
        return this.settings.BannerUseGradient ?? !!(validColor(this.settings.BannerColorEnd) || validColor(this.preset(presets)?.BannerColorEnd));
    }
    setMode(customize: boolean, presets: AppearancePresetEntity[]): void {
        if (this.readOnly || customize === this.customize) { return; }
        if (customize) { this.custom(presets); return; }
        this._customDraft = this.customColors(presets);
        const preset = this.options(presets).find(p => p.AppearancePresetId === (this._presetId || this.settings.AppearancePresetId));
        if (preset) { this.choose(preset, presets); }
        this._mode = 'preset';
    }
    private customColors(presets: AppearancePresetEntity[]): AppearanceSettings {
        const look = this.look(presets);
        return { BannerColorStart: this.colorValue('BannerColorStart', presets), BannerColorEnd: this.colorValue('BannerColorEnd', presets),
            BannerUseGradient: this.useGradient(presets), AvatarBackgroundColor: validColor(look.avatarBackground) || '#B91C1C',
            AvatarTextColor: look.avatarText };
    }
    private patch(values: AppearanceSettings): void {
        if (this.readOnly || !this.formGroup) { return; }
        Object.keys(values).forEach(field => {
            const control = this.formGroup.get(field);
            if (control) { control.setValue(values[field]); control.markAsDirty(); }
        });
        this.formGroup.markAsDirty();
    }
    choose(preset: AppearancePresetEntity, presets: AppearancePresetEntity[]): void {
        if (this.readOnly) { return; }
        if (this.customize) { this._customDraft = this.customColors(presets); }
        const effect = this.look(presets).bannerAnimationKey;
        this.patch({ AppearancePresetId: preset.AppearancePresetId, BannerColorStart: null, BannerColorEnd: null,
            BannerUseGradient: null, AvatarBackgroundColor: null, AvatarTextColor: null, BannerAnimationKey: effect });
        this._presetId = preset.AppearancePresetId;
        this._mode = 'preset'; this.paletteOpen = false;
    }
    custom(presets: AppearancePresetEntity[]): void {
        if (this.readOnly) { return; }
        const look = this.look(presets);
        this._presetId = this.settings.AppearancePresetId || this._presetId;
        const colors = this._customDraft || this.customColors(presets);
        this.patch({ AppearancePresetId: null, ...colors, BannerAnimationKey: look.bannerAnimationKey });
        this._mode = 'custom';
    }
    color(field: 'BannerColorStart' | 'BannerColorEnd' | 'AvatarBackgroundColor' | 'AvatarTextColor', value: string): void {
        this.patch({ [field]: value });
    }
    imageMode(field: 'BannerImageMode' | 'AvatarImageMode', value: string): void {
        this.patch({ [field]: value === '' ? null : Number(value) });
    }
    effect(value: string): void { this.patch({ BannerAnimationKey: value || null }); }
    effectLabel(presets: AppearancePresetEntity[]): string {
        return this.effects.find(effect => effect.key === this.look(presets).bannerAnimationKey)?.label || 'Choose animation';
    }
    gradient(value: boolean, presets: AppearancePresetEntity[] = []): void {
        this.patch({ BannerUseGradient: value,
            ...(value ? { BannerColorEnd: this.colorValue('BannerColorEnd', presets) } : {}) });
    }
    contrastOk(presets: AppearancePresetEntity[]): boolean {
        const look = this.look(presets);
        return !look.avatarBackground.startsWith('#') || avatarContrast(look.avatarBackground, look.avatarText) >= 4.5;
    }
    colorValue(field: 'BannerColorStart' | 'BannerColorEnd' | 'AvatarBackgroundColor' | 'AvatarTextColor', presets: AppearancePresetEntity[]): string {
        return validColor(this.settings[field]) || validColor(this.preset(presets)?.[field])
            || (field === 'AvatarTextColor' ? '#FFFFFF' : field === 'AvatarBackgroundColor' ? '#B91C1C' : '#14532D');
    }
    ngOnDestroy(): void { this._subscriptions.unsubscribe(); }
}
